import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, assetCatalog, fota, registry, type Firmware } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Field } from "@/components/Field";
import { FilePick } from "@/components/FilePick";
import { PageHead } from "@/components/PageHead";
import { Modal } from "@/components/Modal";
import { MultiSelect } from "@/components/MultiSelect";
// The catalogue owns kind labels; importing them beats a second copy
// of the same 13 strings (and since 0064 they come from the DATABASE,
// so a product added in Products is named correctly here for free).
import { kindLabel, useTemplates } from "@/modules/asset_catalog/common";
import { Select, SearchInput } from "@/components/inputs";
import { POLL } from "@/lib/format";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";

import { ActionRow } from "@/components/ActionRow";
const SOFTWARE_LABEL: Record<string, string> = {
  host: "Host controller SW", modem_cell: "Telematics modem SW", ble: "BLE modem SW",
};

/** Firmware library — its own subpage (out of Batch update): a build is
 *  (subcomponent model, software line, version, file), optionally scoped to
 *  a company. The model comes first; the Software options are exactly the
 *  FOTA methods that model's catalog ticks declare. */
export function FotaFirmwarePage() {
  const qc = useQueryClient();
  const fb = useMutationFeedback();
  const fw = useQuery({ queryKey: ["fota", "fw"], queryFn: fota.firmware, refetchInterval: POLL.LIST });
  const pool = useQuery({ queryKey: ["asset-catalog", "models", "pool"], queryFn: () => assetCatalog.models({ limit: "1000" }) });
  const companies = useQuery({ queryKey: ["registry", "companies"], queryFn: registry.companies });
  const tpl = useTemplates().data;

  const flashable = (pool.data?.models ?? []).filter((m) => m.status === "active"
    && Array.isArray((m.derived as { fota_methods?: unknown[] }).fota_methods));

  // PRODUCT FIRST, THEN MODEL. Dattatray: "Before selecting model: we should
  // select the product/kind right? And asset wont be applicable in that list
  // (Products that are not assets!)"
  //
  // Right about the order, and the exclusion is stronger than "not an asset".
  // Checked against the templates: no asset kind declares a FOTA field —
  // and neither do `cell`, `motor` or `dcdc`. So "not an asset" is a
  // CONSEQUENCE, not the rule. The rule is "declares FOTA support", and
  // filtering on the real property rather than a proxy means a product that
  // gains or loses FOTA moves in and out of this list on its own.
  //
  // Derived from the model pool rather than from the templates, so the
  // picker can never offer a product whose model list would then be empty —
  // a dead end is worse than an absence.
  const fotaKinds = Array.from(new Set(flashable.map((m) => m.kind))).sort();
  const [kind, setKind] = useState("");
  const fotaModels = kind ? flashable.filter((m) => m.kind === kind) : flashable;

  const [modelId, setModelId] = useState("");
  const model = fotaModels.find((m) => m.version_id === modelId);
  const methods = ((model?.derived as { fota_methods?: { key: string; software: string; route: string }[] })
    ?.fota_methods) ?? [];
  // The INVERSE of SOFTWARE_LABEL, so the label is doing identifier duty on
  // this one path -- both halves must move together or the picker goes blank.
  const LABEL_KEY: Record<string, string> = { "Host controller SW": "host", "Telematics modem SW": "modem_cell", "BLE modem SW": "ble" };
  const softwares = [...new Set(methods.map((m) => LABEL_KEY[m.software] ?? "host"))];
  const [software, setSoftware] = useState("");
  const [version, setVersion] = useState(1);
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [editScope, setEditScope] = useState<Firmware | null>(null);
  const [binFile, setBinFile] = useState<File | null>(null);

  const register = useMutation({
    mutationFn: async () => {
      if (!binFile) throw new Error("choose a .bin / .hex file");
      const form = new FormData();
      form.append("file", binFile);
      form.append("product_id", model ? model.name : "unknown");
      form.append("version", String(version));
      form.append("channel", "stable");
      form.append("software", software);
      if (modelId) form.append("version_id", modelId);
      if (companyIds.length) form.append("company_ids", companyIds.join(","));
      return fota.registerFirmware(form);
    },
    onSuccess: () => { fb.ok(); setBinFile(null); setCompanyIds([]); void qc.invalidateQueries({ queryKey: ["fota", "fw"] }); },
    onError: fb.fail,
  });
  const removeFw = useMutation({
    mutationFn: (id: string) => fota.deleteFirmware(id),
    onSuccess: () => { fb.ok(); void qc.invalidateQueries({ queryKey: ["fota", "fw"] }); },
    onError: (e) => fb.fail(e instanceof ApiError && e.status === 409
      ? new ApiError(409, "FIRMWARE_IN_USE", "Can't delete - a rollout uses this firmware.") : e),
  });

  const modelName = (id: string | null) => fotaModels.find((m) => m.version_id === id)?.name
    ?? (pool.data?.models ?? []).find((m) => m.version_id === id)?.name ?? null;
  const companyName = (id: string | null) => (companies.data?.companies ?? [])
    .find((c) => c.company_id === id)?.name ?? null;
  const dim = <span style={{ color: "var(--t-dim)" }}>—</span>;
  const cols = useMemo<DataCol<Firmware>[]>(() => [
    { key: "sw", label: "Software", width: 130, render: (f) => SOFTWARE_LABEL[f.software] ?? f.software },
    { key: "model", label: "Subcomponent model", width: 180, render: (f) => f.version_id ? (modelName(f.version_id) ?? f.version_id.slice(0, 8)) : f.product_id },
    { key: "ver", label: "Version", width: 70, render: (f) => <span className="dev">v{f.version}</span> },
    { key: "file", label: "File", width: 180, mono: true, render: (f) => f.filename ?? dim },
    { key: "company", label: "Released to", width: 190, render: (f) => f.companies.length
        ? f.companies.map((c) => companyName(c) ?? c).join(", ")
        : "All companies",
      title: (f) => f.companies.map((c) => companyName(c) ?? c).join(", ") || "All companies" },
    { key: "size", label: "Size", width: 80, render: (f) => `${(f.size / 1024).toFixed(1)} KB` },
    { key: "sha", label: "SHA-256", width: 150, mono: true, title: (f) => f.sha256, render: (f) => f.sha256.slice(0, 12) + "…" },
    { key: "act", label: "", width: 140, render: (f) => (
      <span style={{ display: "flex", gap: 6 }}>
        <button className="btn ghost" onClick={(e) => { e.stopPropagation(); setEditScope(f); }}>Edit</button>
        <button className="btn red" onClick={(e) => { e.stopPropagation(); removeFw.mutate(f.id); }}>Delete</button>
      </span>) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [pool.data, companies.data]);

  return (
    <div className="mpage on">
      <PageHead eyebrow="FOTA · Firmware" title="Firmware library"
        desc="A build = model + software line + version + file. Narrow by product, then pick the model - the Software options are exactly the FOTA methods its catalog ticks declare. Only products that declare FOTA appear. Scope to a company for customer-specific builds." />

      <div className="section-label">Upload a build</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 14 }}>
          <Field label="Product">
            <Select className="fill" value={kind}
              onChange={(e) => { setKind(e.target.value); setModelId(""); setSoftware(""); }}>
              {/* Only products that declare FOTA. Assets are absent because
                  none of them do - not because assets are excluded by name. */}
              <option value="">All flashable products</option>
              {fotaKinds.map((k) => (
                <option key={k} value={k}>{kindLabel(tpl, k)}</option>
              ))}
            </Select>
          </Field>
          <Field label="Model" req>
            <Select className="fill" value={modelId} onChange={(e) => { setModelId(e.target.value); setSoftware(""); }}>
              <option value="">{fotaModels.length ? "choose"
                : kind ? "no active model of this product declares FOTA"
                  : "no active model declares FOTA support"}</option>
              {fotaModels.map((m) => <option key={m.version_id} value={m.version_id}>{m.name} · {m.manufacturer}</option>)}
            </Select>
          </Field>
          <Field label="Software" req>
            <Select className="fill" value={software} disabled={!modelId} onChange={(e) => setSoftware(e.target.value)}>
              <option value="">{modelId ? "choose" : "model first"}</option>
              {softwares.map((sw) => <option key={sw} value={sw}>{SOFTWARE_LABEL[sw]}</option>)}
            </Select>
          </Field>
          <Field label="Version" req>
            <SearchInput className="fill" type="number" mono value={version} min={1}
              onChange={(e) => setVersion(Number(e.target.value))} />
          </Field>
          <Field label="Released to">
            <MultiSelect options={(companies.data?.companies ?? []).map((c) => ({ value: c.company_id, label: c.name }))}
              value={companyIds} onChange={setCompanyIds} emptyLabel="All companies" clearLabel="Clear - all companies" />
          </Field>
          <Field label="Binary (.bin / .hex)" req>
            <FilePick accept=".bin,.hex" label="Choose file" fileName={binFile?.name ?? ""} onFile={setBinFile} />
          </Field>
        </div>
        <ActionRow>
          {fb.errorNode}
          <button className="btn teal" disabled={!modelId || !software || !binFile || register.isPending}
            onClick={() => register.mutate()}>Upload build</button>
        </ActionRow>
      </div>

      {editScope && (
        <EditScopeModal fw={editScope} companies={companies.data?.companies ?? []}
          onClose={() => setEditScope(null)}
          onSaved={() => { setEditScope(null); void qc.invalidateQueries({ queryKey: ["fota", "fw"] }); }} />
      )}

      <div className="section-label">Builds</div>
      <DataTable rows={fw.data?.firmware ?? []} cols={cols} rowKey={(f) => f.id} pageSize={50}
        empty={<div className="panel panel-pad cap">
          No builds yet - upload the first one above.
        </div>} />
    </div>
  );
}


/** Edit a build's release scope: re-allocate to more companies, restrict, or
 *  open to all (empty selection). Rollouts outside the scope are refused. */
function EditScopeModal({ fw, companies, onClose, onSaved }: {
  fw: Firmware; companies: { company_id: string; name: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const fb = useMutationFeedback();
  const [ids, setIds] = useState<string[]>(fw.companies);
  const save = useMutation({
    mutationFn: () => fota.setFirmwareCompanies(fw.id, ids),
    onSuccess: () => { fb.ok(); onSaved(); },
    onError: fb.fail,
  });
  return (
    <Modal title={`Release scope - v${fw.version} (${fw.filename ?? fw.product_id})`} onClose={onClose}
      footer={<>
        {fb.errorNode}
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn teal" disabled={save.isPending} onClick={() => save.mutate()}>Save scope</button>
      </>}>
      <div style={{ fontSize: 12.5, color: "var(--t-mute)", marginBottom: 12 }}>
        Companies this build is released to. Empty = released to <b>all</b> companies.
        A rollout can only target companies inside this scope.
      </div>
      <MultiSelect options={companies.map((c) => ({ value: c.company_id, label: c.name }))}
        value={ids} onChange={setIds} emptyLabel="All companies" clearLabel="Clear - all companies" />
    </Modal>
  );
}
